import React, { useState, useEffect, useRef } from 'react'
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, DragMoveEvent } from '@dnd-kit/core'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppSelector, useAppDispatch } from '../../../store/index.ts'
import { setAuthenticatedPlanner, clearAuth, saveSession, setLoading, setError, setGuests, setSeatingPlan, assignGuestToTable, updateTablePosition, clearPendingChanges, loadAuthFromStorage, startOnboarding, nextOnboardingStep, skipOnboarding, addTable } from '../../../store/seatingSlice.ts'
import { loginWithPlannerPin, fetchPlannerGuests, fetchSeatingPlan, updateGuestEndpoint, updateSeatingPlanEndpoint } from '../../../endpoint/index.ts'
import { IGuest, ITable, TableShape } from '../../../interface/index.ts'
import PinLoginForm from '../../../components/molecule/pinLoginForm/index.tsx'
import GuestListPanel from '../../../components/organism/guestListPanel/index.tsx'
import FloorPlanCanvas from '../../../components/organism/floorPlanCanvas/index.tsx'
import SeatingToolbar from '../../../components/organism/seatingToolbar/index.tsx'
import OnboardingTooltip from '../../../components/molecule/onboardingTooltip/index.tsx'
import MobileTabBar, { TabType } from '../../../components/molecule/mobileTabBar/index.tsx'
import GuestSelectorModal from '../../../components/molecule/guestSelectorModal/index.tsx'
import RsvpLinkPanel from '../../../components/molecule/rsvpLinkPanel/index.tsx'
import PozivnicaPanel from '../../../components/organism/pozivnicaPanel/index.tsx'
import { useMobileDetection } from '../../../hooks/useMobileDetection.ts'
import './index.css'

// Drag overlay component for visual feedback
const GuestDragPreview: React.FC<{ guest: IGuest }> = ({ guest }) => (
	<div className='guest-drag-preview'>
		{guest.firstName.charAt(0)}{guest.lastName.charAt(0)}
	</div>
)

const TableDragPreview: React.FC<{ table: ITable }> = ({ table }) => (
	<div className={`table-drag-preview ${table.shape}`}>
		<span>{table.name}</span>
	</div>
)

const WeddingPlaner: React.FC = () => {
	const { plannerId } = useParams<{ plannerId: string }>()
	const navigate = useNavigate()
	const dispatch = useAppDispatch()
	const { t } = useTranslation('planner')
	const { authenticatedPlanner, guests, seatingPlan, loading, error, pendingChanges, hasCompletedOnboarding, onboardingStep } = useAppSelector(({ seating }) => seating)
	const { isMobile } = useMobileDetection()

	const [activeItem, setActiveItem] = useState<{ type: 'guest' | 'table'; data: IGuest | ITable } | null>(null)
	const [dragTargetPosition, setDragTargetPosition] = useState<{ x: number; y: number; tableId: string } | null>(null)
	const [isSaving, setIsSaving] = useState<boolean>(false)
	const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	// Mobile state
	const [activeTab, setActiveTab] = useState<TabType>('floorPlan')
	const [seatSelectionModal, setSeatSelectionModal] = useState<{
		show: boolean
		table: ITable | null
		seatIndex: number | null
	}>({ show: false, table: null, seatIndex: null })

	// Refs for onboarding targets
	const guestListRef = useRef<HTMLDivElement>(null)
	const addTableButtonsRef = useRef<HTMLDivElement>(null)
	const canvasRef = useRef<HTMLDivElement>(null)
	const fabButtonRef = useRef<HTMLDivElement>(null)

	// Restore auth session from localStorage on mount, then fetch fresh data from Firebase
	useEffect(() => {
		const restoreSession = async () => {
			const stored = loadAuthFromStorage()
			if (stored && !authenticatedPlanner) {
				// If URL has plannerId and it doesn't match stored session, redirect to login
				if (plannerId && stored.authenticatedPlanner.id !== plannerId) {
					navigate('/weddingPlanner/login')
					return
				}
				dispatch(setAuthenticatedPlanner(stored.authenticatedPlanner))
				dispatch(setLoading(true))
				try {
					// Fetch fresh guests and seating plan from Firebase
					const [guestsData, seatingPlanData] = await Promise.all([
						fetchPlannerGuests(stored.authenticatedPlanner.id),
						fetchSeatingPlan(stored.authenticatedPlanner.id)
					])
					dispatch(setGuests(guestsData))
					dispatch(setSeatingPlan(seatingPlanData))
				} catch (err) {
					console.error('Error fetching session data:', err)
				} finally {
					dispatch(setLoading(false))
				}
			}
		}
		restoreSession()
	}, [plannerId, navigate])

	// Auto-save seating plan when changes are pending
	useEffect(() => {
		if (!pendingChanges || !authenticatedPlanner || !seatingPlan) return

		// Clear existing timeout
		if (saveTimeoutRef.current) {
			clearTimeout(saveTimeoutRef.current)
		}

		// Debounced save after 1 second
		saveTimeoutRef.current = setTimeout(async () => {
			setIsSaving(true)
			try {
				const success = await updateSeatingPlanEndpoint(authenticatedPlanner.id, {
					tables: seatingPlan.tables
				})
				if (success) {
					dispatch(clearPendingChanges())
					dispatch(saveSession()) // Sync updated positions to localStorage
				}
			} catch (error) {
				console.error('Auto-save error:', error)
			} finally {
				setIsSaving(false)
			}
		}, 1000)

		return () => {
			if (saveTimeoutRef.current) {
				clearTimeout(saveTimeoutRef.current)
			}
		}
	}, [pendingChanges, authenticatedPlanner, seatingPlan, dispatch])

	// Start onboarding for new users after login
	useEffect(() => {
		if (authenticatedPlanner && !hasCompletedOnboarding && onboardingStep === 0) {
			// Small delay to let UI settle
			const timeoutId = setTimeout(() => {
				dispatch(startOnboarding())
			}, 800)
			return () => clearTimeout(timeoutId)
		}
	}, [authenticatedPlanner, hasCompletedOnboarding, onboardingStep, dispatch])

	// Get the current target ref for onboarding (different for mobile vs desktop)
	const getOnboardingTargetRef = () => {
		if (isMobile) {
			// Mobile steps: 1=welcome (no target), 2=FAB button, 3=canvas (no target), 4=status (no target)
			switch (onboardingStep) {
				case 2: return fabButtonRef // FAB button for adding tables
				default: return canvasRef // Fallback, but these steps don't need highlight
			}
		} else {
			// Desktop steps: 1=guest list, 2=add table buttons, 3=canvas, 4=guest status
			switch (onboardingStep) {
				case 1: return guestListRef
				case 2: return addTableButtonsRef
				case 3: return canvasRef
				case 4: return guestListRef
				default: return guestListRef
			}
		}
	}

	const handlePinSubmit = async (pin: string) => {
		dispatch(setLoading(true))
		dispatch(setError(null))

		try {
			const planner = await loginWithPlannerPin(pin)

			if (planner) {
				dispatch(setAuthenticatedPlanner(planner))

				// Fetch guests and seating plan for this planner
				const [guestsData, seatingPlanData] = await Promise.all([
					fetchPlannerGuests(planner.id),
					fetchSeatingPlan(planner.id)
				])

				dispatch(setGuests(guestsData))
				dispatch(setSeatingPlan(seatingPlanData))
				// Save session to localStorage for persistence
				dispatch(saveSession())
			} else {
				dispatch(setError(t('login.errors.invalidPin')))
			}
		} catch (err) {
			dispatch(setError(t('login.errors.generic')))
			console.error('Login error:', err)
		} finally {
			dispatch(setLoading(false))
		}
	}

	const handleLogout = () => {
		dispatch(clearAuth())
		navigate('/weddingPlanner/login')
	}

	const handleDragStart = (event: DragStartEvent) => {
		const { active } = event
		if (active.data.current?.type === 'guest') {
			setActiveItem({ type: 'guest', data: active.data.current.guest })
		} else if (active.data.current?.type === 'tablePosition') {
			setActiveItem({ type: 'table', data: active.data.current.table })
		}
	}

	const handleDragMove = (event: DragMoveEvent) => {
		const { active, delta } = event

		// Only track position for table drags
		if (active.data.current?.type === 'tablePosition' && delta) {
			const table = active.data.current.table as ITable

			const canvas = document.querySelector('.floor-plan-area')
			if (canvas) {
				const rect = canvas.getBoundingClientRect()
				const deltaXPercent = (delta.x / rect.width) * 100
				const deltaYPercent = (delta.y / rect.height) * 100

				// No constraints - allow infinite positioning
				const newX = table.position.x + deltaXPercent
				const newY = table.position.y + deltaYPercent

				setDragTargetPosition({ x: newX, y: newY, tableId: table.id })
			}
		}
	}

	const handleDragEnd = async (event: DragEndEvent) => {
		setActiveItem(null)
		setDragTargetPosition(null)
		const { active, over, delta } = event

		// Handle guest being dropped on a specific seat
		if (active.data.current?.type === 'guest' && over?.data.current?.type === 'seat') {
			const guest = active.data.current.guest as IGuest
			const { table, seatIndex } = over.data.current as { table: ITable; seatIndex: number }

			// Check if seat is already occupied
			const seatOccupied = guests.find(g => g.tableId === table.id && g.seatPosition === seatIndex)
			if (seatOccupied) {
				alert(t('app.alerts.seatOccupied'))
				return
			}

			dispatch(assignGuestToTable({ guestId: guest.id, tableId: table.id, seatPosition: seatIndex }))
			if (authenticatedPlanner) {
				await updateGuestEndpoint(guest.id, { tableId: table.id, seatPosition: seatIndex }, authenticatedPlanner.id)
			}
			return
		}

		// Handle guest being dropped on a table (assign to first available seat)
		if (active.data.current?.type === 'guest' && over?.data.current?.type === 'table') {
			const guest = active.data.current.guest as IGuest
			const table = over.data.current.table as ITable

			// Check if table has capacity
			const tableGuests = guests.filter(g => g.tableId === table.id)
			if (tableGuests.length >= table.capacity) {
				alert(t('app.alerts.tableFull'))
				return
			}

			// Find first empty seat position
			let firstEmptySeat = 0
			for (let i = 0; i < table.capacity; i++) {
				if (!tableGuests.find(g => g.seatPosition === i)) {
					firstEmptySeat = i
					break
				}
			}

			dispatch(assignGuestToTable({ guestId: guest.id, tableId: table.id, seatPosition: firstEmptySeat }))
			if (authenticatedPlanner) {
				await updateGuestEndpoint(guest.id, { tableId: table.id, seatPosition: firstEmptySeat }, authenticatedPlanner.id)
			}
		}

		// Handle table being repositioned
		if (active.data.current?.type === 'tablePosition' && delta) {
			const table = active.data.current.table as ITable

			// Convert pixel delta to percentage (accounting for zoom)
			const canvas = document.querySelector('.floor-plan-area')
			if (canvas) {
				const rect = canvas.getBoundingClientRect()
				// Divide by zoom since rect.width/height already includes the zoom scale
				const deltaXPercent = (delta.x / rect.width) * 100
				const deltaYPercent = (delta.y / rect.height) * 100

				// No constraints - allow infinite positioning
				const newX = table.position.x + deltaXPercent
				const newY = table.position.y + deltaYPercent

				dispatch(updateTablePosition({
					tableId: table.id,
					position: { x: newX, y: newY }
				}))
			}
		}
	}

	// Mobile: Handle seat tap to open guest selector
	const handleSeatTap = (table: ITable, seatIndex: number) => {
		setSeatSelectionModal({ show: true, table, seatIndex })
	}

	// Mobile: Handle guest selection from modal
	const handleGuestSelect = async (guest: IGuest) => {
		if (!seatSelectionModal.table || seatSelectionModal.seatIndex === null) return

		dispatch(assignGuestToTable({
			guestId: guest.id,
			tableId: seatSelectionModal.table.id,
			seatPosition: seatSelectionModal.seatIndex
		}))

		if (authenticatedPlanner) {
			await updateGuestEndpoint(
				guest.id,
				{ tableId: seatSelectionModal.table.id, seatPosition: seatSelectionModal.seatIndex },
				authenticatedPlanner.id
			)
		}
	}

	// Mobile: Handle adding table from FAB
	const handleAddTable = (shape: TableShape) => {
		const tableCount = seatingPlan?.tables.length || 0
		const canvas = document.querySelector('.floor-plan-canvas')
		let centerX = 50
		let centerY = 50

		if (canvas) {
			const rect = canvas.getBoundingClientRect()
			const viewportCenterX = rect.left + rect.width / 2
			const viewportCenterY = rect.top + rect.height / 2
			const canvasArea = canvas.querySelector('.floor-plan-area')
			if (canvasArea) {
				const areaRect = canvasArea.getBoundingClientRect()
				centerX = ((viewportCenterX - areaRect.left) / areaRect.width) * 100
				centerY = ((viewportCenterY - areaRect.top) / areaRect.height) * 100
			}
		}

		const newTable = {
			id: `table-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
			name: `${t('app.table')} ${tableCount + 1}`,
			shape,
			capacity: shape === 'circle' ? 8 : 10,
			position: { x: centerX, y: centerY },
			rotation: 0
		}

		dispatch(addTable(newTable))
	}

	// Calculate unassigned guest count for tab badge
	const unassignedCount = guests.filter(g => !g.tableId).length

	// Show PIN login if not authenticated
	if (!authenticatedPlanner) {
		return (
			<div className='wedding-planer-container'>
				<div className='wedding-planer-login'>
					<div className='wedding-planer-login-card'>
						<img
							src={require('../../../assets/weddingCamera/logo-with-text.png')}
							alt='Wedding Camera Logo'
							className='wedding-planer-logo'
						/>
						<h1>{t('app.title')}</h1>
						<p>{t('app.pinPrompt')}</p>
						<PinLoginForm
							onSubmit={handlePinSubmit}
							loading={loading}
							error={error}
						/>
					</div>
				</div>
			</div>
		)
	}

	// Show main planner view
	const weddingTitle = `${authenticatedPlanner.brideName} & ${authenticatedPlanner.groomName}`

	// Mobile layout: Tab-based navigation
	if (isMobile) {
		return (
			<DndContext onDragStart={handleDragStart} onDragMove={handleDragMove} onDragEnd={handleDragEnd}>
				<div className='wedding-planer-container mobile'>
					<SeatingToolbar
						weddingTitle={weddingTitle}
						onLogout={handleLogout}
						isSaving={isSaving}
						isMobile={true}
					/>

					<RsvpLinkPanel plannerId={plannerId} />

					{/* Pozivnica dobiva istu klasu kao na desktopu: mobilni spremnik je
					    `overflow: hidden` zbog plana sale, a obrazac pozivnice je dulji
					    od ekrana pa bi ostao odsjecen. */}
					<div className={`wedding-planer-content mobile-content${activeTab === 'pozivnica' ? ' pozivnica-content' : ''}`}>
						{activeTab === 'guests' && (
							<GuestListPanel
								ref={guestListRef}
								guests={guests}
								plannerId={authenticatedPlanner.id}
								isMobile={true}
							/>
						)}
						{activeTab === 'floorPlan' && (
							<FloorPlanCanvas
								ref={canvasRef}
								seatingPlan={seatingPlan}
								guests={guests}
								dragTargetPosition={dragTargetPosition}
								plannerId={authenticatedPlanner.id}
								isMobile={true}
								onSeatTap={handleSeatTap}
								onAddTable={handleAddTable}
								fabButtonRef={fabButtonRef}
							/>
						)}
						{activeTab === 'pozivnica' && (
							<PozivnicaPanel plannerId={authenticatedPlanner.id} />
						)}
					</div>

					<MobileTabBar
						activeTab={activeTab}
						onTabChange={setActiveTab}
						unassignedCount={unassignedCount}
					/>
				</div>

				{/* Guest Selector Modal for tap-to-assign */}
				<GuestSelectorModal
					show={seatSelectionModal.show}
					onHide={() => setSeatSelectionModal({ show: false, table: null, seatIndex: null })}
					guests={guests}
					table={seatSelectionModal.table}
					seatIndex={seatSelectionModal.seatIndex}
					onSelectGuest={handleGuestSelect}
				/>

				{/* Onboarding tooltip for new users */}
				{onboardingStep > 0 && onboardingStep <= 4 && (
					<OnboardingTooltip
						step={onboardingStep}
						targetRef={getOnboardingTargetRef()}
						onNext={() => dispatch(nextOnboardingStep())}
						onSkip={() => dispatch(skipOnboarding())}
						totalSteps={4}
						isMobile={true}
					/>
				)}
			</DndContext>
		)
	}

	// Desktop layout: Side-by-side
	return (
		<DndContext onDragStart={handleDragStart} onDragMove={handleDragMove} onDragEnd={handleDragEnd}>
			<div className='wedding-planer-container'>
				<SeatingToolbar
					weddingTitle={weddingTitle}
					onLogout={handleLogout}
					isSaving={isSaving}
					addTableButtonsRef={addTableButtonsRef}
				/>

				<RsvpLinkPanel plannerId={plannerId} />

				{/* Prebacivanje između plana sale i pozivnice. Ide ispod gornjeg
				  * reda (alatna traka + RSVP traka), nikad u njega - pozivnica je
				  * treći tab, ne novi red iznad plana sale koji je paru već
				  * jednom pojeo fokus. */}
				<div className='planner-tabs' role='tablist'>
					<button
						type='button'
						role='tab'
						aria-selected={activeTab !== 'pozivnica'}
						className={`planner-tab ${activeTab !== 'pozivnica' ? 'active' : ''}`}
						onClick={() => setActiveTab('floorPlan')}
					>
						{t('tabs.plan')}
					</button>
					<button
						type='button'
						role='tab'
						aria-selected={activeTab === 'pozivnica'}
						className={`planner-tab ${activeTab === 'pozivnica' ? 'active' : ''}`}
						onClick={() => setActiveTab('pozivnica')}
					>
						{t('tabs.pozivnica')}
					</button>
				</div>

				{activeTab === 'pozivnica'
					? (
						<div className='wedding-planer-content pozivnica-content'>
							<PozivnicaPanel plannerId={authenticatedPlanner.id} />
						</div>
					)
					: (
						<div className='wedding-planer-content'>
							<GuestListPanel ref={guestListRef} guests={guests} plannerId={authenticatedPlanner.id} />
							<FloorPlanCanvas
								ref={canvasRef}
								seatingPlan={seatingPlan}
								guests={guests}
								dragTargetPosition={dragTargetPosition}
								plannerId={authenticatedPlanner.id}
								onSeatTap={handleSeatTap}
							/>
						</div>
					)}
			</div>

			{/* Guest Selector Modal for click-to-assign on desktop */}
			<GuestSelectorModal
				show={seatSelectionModal.show}
				onHide={() => setSeatSelectionModal({ show: false, table: null, seatIndex: null })}
				guests={guests}
				table={seatSelectionModal.table}
				seatIndex={seatSelectionModal.seatIndex}
				onSelectGuest={handleGuestSelect}
			/>

			<DragOverlay dropAnimation={null}>
				{activeItem?.type === 'guest' && (
					<GuestDragPreview guest={activeItem.data as IGuest} />
				)}
				{activeItem?.type === 'table' && (
					<TableDragPreview table={activeItem.data as ITable} />
				)}
			</DragOverlay>

			{/* Onboarding tooltip for new users */}
			{onboardingStep > 0 && onboardingStep <= 4 && (
				<OnboardingTooltip
					step={onboardingStep}
					targetRef={getOnboardingTargetRef()}
					onNext={() => dispatch(nextOnboardingStep())}
					onSkip={() => dispatch(skipOnboarding())}
					totalSteps={4}
				/>
			)}
		</DndContext>
	)
}

export default WeddingPlaner
